'use client';

import { Fragment, useEffect, useState, type ReactNode } from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Crosshair,
  Minus,
  Plus,
  RotateCcw,
} from 'lucide-react';
import { cameraCommandAvailability, type CommandRoute } from '@/lib/drone-command-availability';
import type { AckPhase } from '@/lib/command-ack';
import type { DroneCommandParams } from '@/hooks/useDroneCommand';
import type { LiveDrone } from '@/hooks/useWebSocket';
import type { CameraLens, CompanionStatus, PayloadCapabilities } from '@/types/fleet';
import {
  formatLensReadout,
  formatPitchReadout,
  formatYawReadout,
  formatZoomReadout,
  LENS_LABELS,
  LENS_ORDER,
  useCameraGestures,
} from './useCameraGestures';
import { playsPicture } from './VideoStreamBody';
import type { CopVideoStream } from './types';

/**
 * Camera control — the gesture surface and the bar — over whichever picture the
 * operator is looking at.
 *
 * Lifted out of CopVideoWindow so that control follows the picture instead of
 * living at one address. Both of its homes draw the tile they slew: the
 * floating windows over the map, and the stage (manual flight, and the split
 * beside the map). The details panel carried one too while it drew a picture
 * inline; when the picture moved out to a window the overlay went with it,
 * because the rule this component is named for cuts both ways — a gimbal
 * commanded from a surface that is NOT showing what it sees is aimed blind.
 *
 * **Be precise about what is and is not exclusive here.** A single CAMERA is
 * drawn in one place at a time — `chooseStream` makes the stage skip whatever a
 * window is already showing — but the SURFACES are not mutually exclusive: a
 * manual session and the floating windows are independently open, and a
 * multi-camera airframe can have one window per camera. So several overlays for
 * one aircraft can be mounted at once. They cannot disagree — every one is
 * handed the same `cameraControl` object, assembled once in page.tsx — but
 * anything here that must exist exactly ONCE per aircraft (the `aria-live`
 * failure region is the live example, currently duplicated) cannot rely on the
 * layout to make it so.
 *
 * Contention between two OPERATORS was never the layout's job anyway: it is a
 * server-side question, answered by the same route/role/liveness gates every
 * camera verb already passes through, not by which div the buttons are in.
 *
 * Drop it inside a `relative` box that also holds the picture; it positions
 * itself over the bottom of that box. Every gate, every refusal string and
 * every review finding below (F2, F3a-d, F4, F8, F9) came with it from the
 * window and is unchanged — one component, so the surfaces cannot drift apart.
 */

export interface CameraControlInputs {
  /** The executor the API routes this drone's commands to, from
   *  `commandRouteOf(registry row)`. Camera verbs fly only on `dji_companion`;
   *  null (registry loading/failed) withholds the controls. */
  commandRoute?: CommandRoute | null;
  /** From the companion heartbeat. Null / absent = not reported — controls
   *  withheld with the reason on screen. */
  payloadCapabilities?: PayloadCapabilities | null;
  /** Live camera pose/zoom from telemetry. Absent = not reported → "—". */
  gimbalPitchDeg?: number;
  gimbalYawDeg?: number;
  zoomRatio?: number;
  /** The lens the CAMERA reports, from telemetry. Absent = not reported → "—",
   *  never assumed to be wide: no lens key is drawn as confirmed on an unknown
   *  lens, because "we have not been told" and "it is wide" are different facts
   *  and only one of them means the operator is looking at the wide picture. */
  cameraLens?: CameraLens | null;
  /** Role gate — admin or operator. */
  canOperate?: boolean;
  /** Why THIS SURFACE has no controls, when the reason is not about the
   *  aircraft, the link or the role. Overrides every other gate, so a window
   *  that simply is not the selected aircraft says so instead of borrowing a
   *  refusal that would be a lie. See `cameraCommandAvailability`. */
  controlWithheldReason?: string | null;
  /** `LiveDrone.telemetry_source` — 'remoteid' withholds all controls (F1). */
  telemetrySource?: LiveDrone['telemetry_source'];
  /** `LiveDrone.last_seen_at`, for the same link-freshness gate the flight bar
   *  runs (F1). Absent = no live track bound; the gate is skipped. */
  lastSeenAt?: string | null;
  /** Companion-app liveness — a dead or sweep-flipped companion locks all
   *  camera controls with the reason while the readout stays (F1). */
  companion?: Pick<CompanionStatus, 'is_online' | 'stale'> | null;
  /** Test clock override; when absent the overlay ticks at 1 Hz so freshness
   *  keeps advancing after telemetry stops. */
  now?: number;
  /** Sends a camera verb through the selected drone's command controller.
   *  Absent = no control at all, whatever the capabilities claim. */
  onCameraCommand?: (action: 'gimbal' | 'zoom' | 'lens', params: DroneCommandParams) => void;
  /** Ack phases of the last gimbal / zoom / lens command, for the readout glyph. */
  gimbalPhase?: AckPhase | null;
  zoomPhase?: AckPhase | null;
  lensPhase?: AckPhase | null;
  /** Failure detail of the last gimbal / zoom / lens command (useDroneCommand's
   *  failureOf). Rendered as a visible line, not a tooltip — tooltips do not
   *  exist on touch (F3). Auto-clears when the hook retires the failed entry. */
  gimbalFailure?: string | null;
  zoomFailure?: string | null;
  lensFailure?: string | null;
  /**
   * The lens the operator last PRESSED — not the one the camera reports.
   *
   * Both values exist deliberately, and the control is misleading with only one.
   * A lens key is a REQUEST the aircraft either honours or does not, so a key
   * that lit up on press would assert a picture change that may never have
   * happened. `cameraLens` (telemetry) is the only evidence the lens actually
   * changed; this exists solely to render the gap between the two while a
   * command is in flight, and to stop a stale press reading as the live lens.
   * The RC's own LENS row separates them the same way — this mirrors it rather
   * than inventing a second idiom for one camera.
   */
  requestedLens?: CameraLens | null;
}

export interface CameraControlOverlayProps extends CameraControlInputs {
  /** The stream painted underneath. Decides whether the gesture surface may be
   *  attached at all — see the comment at its render. */
  stream: CopVideoStream | null;
  /**
   * Whether the player under this overlay is actually PAINTING right now.
   *
   * `playsPicture(stream)` alone is not enough and never was. It answers "could
   * a browser paint this URL", which stays true of an HLS stream the player has
   * already given up on — and `HlsVideo` covers a dead player with a
   * `pointer-events-none` "Stream offline" tile, so the gesture surface (a later
   * sibling) still wins the press. An operator could drag a black tile and
   * silently slew a live gimbal, which is the exact hazard F9 claims to prevent.
   *
   * Defaults to true so a caller that genuinely cannot know (a plain `.mp4`
   * element, which has no such state) is unchanged; every caller that CAN know
   * passes it.
   */
  pictureLive?: boolean;
  /**
   * Whether the picture may be GRABBED (drag to slew, pinch to zoom).
   *
   * CURRENTLY PASSED BY NOBODY — every surface that draws a picture is now one
   * where grabbing is safe, so the default carries them all. Kept because the
   * hazard it guards is real and the next scrolling surface to draw a picture
   * will need it again.
   *
   * Off for any surface inside a scroll container. The gesture surface is
   * `touch-action: none`, which suppresses the browser's own panning for touches
   * starting on it — harmless in a floating window or on the manual stage, both
   * fixed-position, and harmful in the details panel, which is `overflow-y-auto`.
   * While that panel drew a picture it passed `false`: a tablet operator swiping
   * up over the tile to read the Status/Protocol/Latency rows beneath it would
   * not have scrolled, the swipe would have cleared the 6 px dead zone and
   * commanded roughly 0.16°/px of pitch — about 32° for a 200 px swipe. The
   * panel draws no picture any more, so the case is gone rather than solved.
   *
   * The BAR is unaffected by this and renders wherever the overlay does.
   */
  gestures?: boolean;
  /**
   * Tighter type and padding for the corner picture-in-picture, matching
   * `VideoLatencyBadge`'s prop of the same name.
   *
   * The manual stage's MAP layout — the default — is a 288 px box with ~139 px
   * of picture under its header. At full size this stack can take most of that
   * and leave a strip of video, so the surface that is smallest is exactly the
   * one that must not render at full size.
   */
  compact?: boolean;
  /**
   * Anything else that belongs at the bottom of this picture, rendered UNDER
   * the control bar inside the same bottom-anchored stack.
   *
   * It exists because the manual-flight stage already anchors the latency badge
   * to `bottom-0`, and two elements claiming that slot is a z-index fight whose
   * winner hides the other — the badge would have covered the readout and the
   * zoom keys. Stacking them in one flex column instead means neither has to
   * know the other's height, which no offset constant could have guaranteed as
   * the badge grows a line for the picture lead.
   */
  footer?: ReactNode;
}

/** The subtle per-send glyphs next to the readout. No toasts for camera verbs —
 *  a toast per zoom tap is noise — so this plus the failure line below the bar
 *  is the whole feedback surface. One glyph PER VERB (G…/Z✗/L✓), never a shared
 *  one: a failed zoom must not be readable as a rejected slew (F3b). */
function verbGlyph(prefix: 'G' | 'Z' | 'L', phase: AckPhase | null | undefined) {
  const verb = prefix === 'G' ? 'Gimbal' : prefix === 'Z' ? 'Zoom' : 'Lens';
  if (phase === 'failed') {
    return <span className="text-on-media-red-fg" title={`${verb} command rejected`}>{`${prefix}✗`}</span>;
  }
  if (phase === 'pending' || phase === 'acked') {
    return <span className="text-on-media-amber-fg" title={`${verb} command in flight`}>{`${prefix}…`}</span>;
  }
  if (phase === 'done') {
    return <span className="text-on-media-green-fg" title={`${verb} command executed`}>{`${prefix}✓`}</span>;
  }
  return null;
}

/**
 * A camera key: 40×40 px, or 32×32 in the corner picture-in-picture.
 *
 * The old 28 px was above WCAG 2.5.8's 24 px floor and below what its own
 * comment claimed — "gloved fingers on a tablet in the field" is 2.5.5's 44 px
 * target, not the minimum. 40 px is what fits three zoom keys across a 288 px
 * PiP without crowding the picture, and it is a real improvement on 28.
 *
 * The keys sit in a grouped panel that owns the border and the ground, so each
 * key is transparent until touched: five separately-bordered boxes in a row was
 * most of what made this bar look like scattered debris.
 *
 * The focus ring is not optional and is easy to forget. These sit over
 * ARBITRARY moving video, so a UA default outline has uncontrolled contrast
 * against whatever frame is underneath; this is the same
 * `focus-visible:outline-*` treatment the rest of the panel uses.
 */
function controlButtonClass(compact: boolean): string {
  return [
    'pointer-events-auto inline-flex shrink-0 items-center justify-center',
    compact ? 'size-8' : 'size-10',
    'text-on-media-fg transition-colors',
    'hover:bg-white/15 active:bg-white/25',
    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
    'disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent',
  ].join(' ');
}

/**
 * How far one nudge key moves the gimbal.
 *
 * The wire contract for `gimbal` is RELATIVE degree deltas — the same one the
 * drag gesture sends — so a nudge is just a fixed delta.
 *
 * 15°, matching `GimbalPitchMath.NUDGE_DEG` on the RC, whose own tilt keys are
 * labelled "TILT UP 15°". One gimbal reached from two surfaces must step by the
 * same amount: an operator who learns the pitch on the controller and then flies
 * from the COP should not have to relearn it, and two constants for one physical
 * quantity is how one of them drifts.
 *
 * NOT clamped here, and the aircraft does not silently clamp either: the SDK
 * answers `SDK_SERVICE_GIMBAL_ROTATE_PITCH_OUT_OF_RANGE` and rotates NOTHING
 * when a relative delta would leave the travel — which on 2026-08-23 left the
 * camera forward for an entire precision-landing flight. From the −90° nadir
 * posture the next "down" press asks for −100° and is refused. The companion
 * already has the fix for this (`GimbalPitchMath.nudgeDelta` applies the step to
 * the CLAMPED current reading, which is what the RC's own keys use); the COP
 * path is the one that bypasses it, and it must be clamped in `gimbalRotate`
 * rather than here — the client is not told the travel limits at all, because
 * `CompanionHeartbeatPublisher` deliberately omits pitch_min_deg/pitch_max_deg.
 */
export const GIMBAL_NUDGE_DEG = 15;

/** The hairline between two keys in a grouped panel. `stacked` for keys that sit
 *  above/below each other rather than side by side. */
function KeyDivider({ stacked = false }: { stacked?: boolean }) {
  return (
    <span aria-hidden className={`shrink-0 bg-white/15 ${stacked ? 'h-px w-5' : 'h-5 w-px'}`} />
  );
}

export function CameraControlOverlay({
  stream,
  pictureLive = true,
  gestures: gesturesEnabled = true,
  compact = false,
  footer = null,
  commandRoute = null,
  payloadCapabilities = null,
  gimbalPitchDeg,
  gimbalYawDeg,
  zoomRatio,
  cameraLens = null,
  canOperate = false,
  controlWithheldReason = null,
  telemetrySource,
  lastSeenAt,
  companion = null,
  now,
  onCameraCommand,
  lensPhase = null,
  lensFailure = null,
  requestedLens = null,
  gimbalPhase = null,
  zoomPhase = null,
  gimbalFailure = null,
  zoomFailure = null,
}: CameraControlOverlayProps) {
  // Freshness must keep advancing when telemetry stops — that is exactly when
  // the liveness gate matters. Same 1 Hz clock the command bar runs, for the
  // same reason: two surfaces disagreeing about whether an aircraft is
  // reachable is what the shared gate exists to prevent (F1).
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (now !== undefined) return;
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [now]);

  const availability = cameraCommandAvailability({
    withheldReason: controlWithheldReason,
    commandRoute,
    capabilities: payloadCapabilities,
    canOperate,
    telemetrySource,
    lastSeenAt,
    companion,
    now: now ?? tick,
  });
  // No send callback = no camera control, whatever the capabilities claim.
  const live = onCameraCommand !== undefined;
  const pitchAvailable = live && availability.gimbalPitch.available;
  const yawAvailable = live && availability.gimbalYaw.available;
  const zoomAvailable = live && availability.zoom.available;
  const lensAvailable = live && availability.lens.available;
  const gimbalUsable = pitchAvailable || yawAvailable;
  const anyUsable = gimbalUsable || zoomAvailable || lensAvailable;

  // What the PAYLOAD has, as opposed to what is reachable right now. The nudge
  // keys render on this and disable on the `*Available` pair above: an axis the
  // payload lacks should never appear, but an axis that exists and is briefly
  // unreachable must grey rather than disappear. `cameraCommandAvailability`
  // folds capability and liveness into one verdict, which is right for "may I
  // send this" and wrong for "should this key exist at all".
  const canPitch = payloadCapabilities?.gimbal_pitch === true;
  const canYaw = payloadCapabilities?.gimbal_yaw === true;

  // Some-but-not-all controls withheld: the reason must be VISIBLE, not a
  // title-only tooltip that touch operators can never see (F3c).
  //
  // But a reason only earns its space when something ON SCREEN would otherwise
  // be puzzling. An axis the AIRFRAME does not have is not a withheld control -
  // there is no key for it, and there never will be on this aircraft, so the
  // line is permanent furniture rather than an explanation. On the Mavic 3T it
  // read "GIMBAL YAW NOT SUPPORTED BY THIS PAYLOAD" on every frame of every
  // flight, directly beneath a readout printing a live yaw ANGLE - which made it
  // look like a contradiction or a fault rather than a hardware fact. F3c is
  // about not HIDING a reason in a tooltip; it does not require standing text
  // for a control that was never offered.
  //
  // Everything else still speaks: a link that dropped, a role that cannot
  // operate, a payload that has not reported yet. Those are states that change,
  // and an operator needs to know why a key they can see is refusing.
  const capabilityAbsent = (c: typeof availability.gimbalPitch, hasAxis: boolean) =>
    !c.available && !hasAxis;
  const partialReasons = anyUsable
    ? Array.from(
        new Set(
          [
            [availability.gimbalPitch, capabilityAbsent(availability.gimbalPitch, canPitch)],
            [availability.gimbalYaw, capabilityAbsent(availability.gimbalYaw, canYaw)],
            [availability.zoom, capabilityAbsent(availability.zoom, payloadCapabilities?.zoom === true)],
          ]
            .filter(([c, absent]) => {
              const control = c as typeof availability.gimbalPitch;
              return !control.available && control.reason !== null && !absent;
            })
            .map(([c]) => (c as typeof availability.gimbalPitch).reason as string),
        ),
      )
    : [];

  const gestures = useCameraGestures({
    pitchAvailable,
    yawAvailable,
    zoomAvailable,
    zoomRatio,
    onGimbal: (params) => onCameraCommand?.('gimbal', params),
    onZoomStep: (step) => onCameraCommand?.('zoom', { step }),
  });

  return (
    <>
      {/* The gesture surface: drag = gimbal slew (drag up = pitch up), pinch
          or double-tap = zoom step. Attached ONLY when a control is usable AND
          there is a picture under it to grab (F9) — unavailable means not sent,
          never rendered-and-failing.

          BOTH halves of that picture test are needed. `playsPicture` rejects the
          branches that were never video: the RTMP branch renders a
          select-to-copy URL, where a drag over the text was slewing a live
          gimbal, and the no-stream branch is instructions over black.
          `pictureLive` rejects the branch that WAS video and has stopped — the
          player's own "Stream offline" tile is `pointer-events-none`, so
          without it this surface still wins the press and an operator drags a
          black rectangle while a real gimbal moves.

          The bar's buttons stay in every case: they are labelled deliberate
          acts, not a picture-grab. touch-none keeps the browser from claiming
          the drag as a scroll or the double-tap as a page zoom. */}
      {anyUsable && gesturesEnabled && playsPicture(stream) && pictureLive && (
        <div
          data-testid="camera-gesture-surface"
          className="absolute inset-0 touch-none cursor-crosshair"
          {...gestures}
        />
      )}

      {/* ONE STACK, not three overlapping corners.

          The readout, the control rail and the bottom band were three separate
          absolutely-positioned boxes, which meant nothing in the layout knew how
          much room any of the others had taken. Measured in a browser at the
          four real surface sizes, that had already cost two collisions: the zoom
          trio lost its bottom ~11 px on the DEFAULT details panel (320 px, before
          any of this work), and a rail tall enough to hold three groups reached
          back across the picture into the pose readout.

          The arithmetic is unforgiving and no amount of tuning fixes it while
          they are independent: the readout is ~244 px wide, three control groups
          are 222 px tall, and the default panel's picture is 296 x 167. As
          siblings in a flex COLUMN they simply cannot overlap - the rail is
          handed what is left after the readout, wraps into a second column when
          that is not enough (see the rail's own comment), and the band reserves
          its height at the bottom instead of being painted over.

          `inset-2` reproduces the old `left-2 top-2` / `right-2 top-2` insets
          exactly, so nothing moves on a picture that was already big enough. */}
      <div className="pointer-events-none absolute inset-2 flex flex-col gap-1.5">
      {/* OBSERVATION, first row. The pose renders in EVERY state — a viewer of a
          dock aircraft (which reports gimbal pose via DJI Cloud OSD) is
          entitled to read it while every control is withheld (F4) — so it must
          never share a row with controls that come and go. `tabular-nums` stops
          the degrees jittering sideways as they count.

          `self-start` keeps it hugging the left edge at its natural width; being
          a flex CHILD rather than an absolute box is what stops the rail ever
          reaching it. */}
      <div
        data-testid="camera-readout"
        className={`flex max-w-full flex-none self-start flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border border-white/15 bg-black/70 backdrop-blur-sm ${
          compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-[11px]'
        } font-mono tabular-nums tracking-wide text-on-media-fg`}
      >
        <span className="select-none whitespace-nowrap">
          {`GBL P ${formatPitchReadout(gimbalPitchDeg)} Y ${formatYawReadout(gimbalYawDeg)}`}
        </span>
        {/* The `·` is not decoration and does not go away because the flex gap
            now separates these visually: `textContent` concatenates, so without
            it a screen reader and every DOM assertion read `Y —ZM —`. */}
        <span className="select-none whitespace-nowrap">
          {`· ZM ${formatZoomReadout(zoomRatio)}`}
        </span>
        {/* The LIVE lens, from telemetry — never the requested one. This line is
            the operator's evidence of what the camera is actually doing, and a
            readout that echoed the press would make the request indistinguishable
            from the result, which is the whole thing the lens keys separate. */}
        <span className="select-none whitespace-nowrap">
          {`· LN ${formatLensReadout(cameraLens)}`}
        </span>
        {verbGlyph('G', gimbalPhase)}
        {verbGlyph('Z', zoomPhase)}
        {verbGlyph('L', lensPhase)}
      </div>

      {/* COMMAND, top-right, grouped by verb so the two gimbal and zoom clusters
          are two things rather than five keys in a line. Top rather than bottom
          because the bottom belongs to the latency badge, which is a safety
          readout and must not be the smallest, dimmest thing on the picture.

          A COLUMN ON THE RIGHT EDGE, not a row across the top. As a row the two
          clusters were laid out right-to-left and their COMBINED width reached
          back across the picture into the pose readout anchored top-left - on a
          real screen the gimbal cross sat mid-frame with `ZM 4.0` disappearing
          behind it. Narrowing the gimbal group to a cross was not enough on its
          own: a row is still as wide as both clusters plus the gap, and that
          total is what has to clear the readout at every panel width.
          Stacked, the cluster is only as wide as the WIDER of the two (the zoom
          trio), it hugs the right edge the way an operator expects a control
          rail to, and no arrangement of the two groups can reach the readout
          again.

          AND IT WRAPS, because a column that only reasons about its WIDTH has
          no idea whether the picture is tall enough to hold it. Three groups on
          a Mavic 3T - `gimbal_yaw` is false, so the cross degenerates to a 3-key
          strip - is 122 + 6 + 40 + 6 + 40 = 214 px of rail, and the default
          details panel offers about 124 px once the readout has taken its row.
          Unwrapped, the lens keys fell entirely below the clip: still focusable,
          still firing, invisible. That is the same failure the WIDTH argument
          above fixed on the other axis, and the zoom trio was quietly losing its
          bottom ~11 px to it before any lens work existed.

          `flex-wrap-reverse` is what sends the overflow somewhere reachable. In
          a COLUMN container the cross axis is horizontal, and `reverse` flips it
          so a wrapped column lands to the LEFT, into the picture, rather than
          off the right edge into the clip. `content-start` then packs those
          columns from that same (right) edge, so the rail grows leftward only as
          far as it must.

          The flip has one cost worth knowing: once the rail WRAPS, DOM order is
          gimbal -> zoom -> lens but the wrapped line lands to the LEFT of the
          first, so an operator reading left-to-right meets zoom and lens before
          gimbal while the tab order still gives them gimbal first. Tolerable
          while the wrap is the fallback rather than the normal state; if a
          surface starts wrapping routinely, reorder the groups instead of
          living with it.

          That flip is also why the alignment reads backwards: with the cross
          axis reversed, `items-start` IS the right edge. It has to be
          `items-start`, not `items-end`, or every group narrower than the widest
          goes flush LEFT and the rail loses the clean edge this paragraph is
          about.

          MEASURED in Chromium at all four real surfaces, rather than reasoned
          about - the reasoning is what got the first two attempts wrong. The
          readout collision is gone at EVERY size, which is the part no amount of
          tuning fixed while the three boxes were independent. With the band
          empty, the default 320 panel and the popped-out window are clean, and
          the 288 px MINIMUM still loses 11 px - which is exactly what `main`
          loses at the DEFAULT width today, so the worst case here is the current
          normal case.

          WHAT IS STILL OVER-CONSTRAINED, and it is arithmetic, not layout: the
          gimbal cross is atomic - 124 px full size, ~100 px compact - and cannot
          wrap into itself. Where the band is POPULATED and the picture is short,
          the rail is handed less than that and the overflow lands behind the
          band's own ground. Two surfaces are in that state:

            - the details panel at its 288 px MINIMUM with a refusal showing;
            - the manual MAP PiP essentially ALWAYS. An earlier version of this
              comment called that surface clean, which was measuring it in a
              state it is rarely in: `ManualVideoStage` passes the latency badge
              whenever a picture is being delivered, so its band is normally
              populated, and the badge is the whole reason that stage exists.

          The band paints after the rail, so it covers the overflowing keys
          rather than the reverse - and it is `pointer-events-none`, so a covered
          key stays clickable while invisible. That is the same failure this
          commit set out to remove, relocated, and it is why the fix is a smaller
          gimbal control on short pictures rather than another flex property - a
          change to a control this PR does not own.

          One more constraint to write down before someone trips on it: the
          two-column WIDTH arithmetic above holds only while `gimbal_yaw` is
          false, which the heartbeat publisher hard-codes as a provisional safe
          default. Reported true, the cross becomes 122 px WIDE, the wrapped rail
          is ~276 px against ~248 px of content width at the 288 px minimum, and
          the overflow goes off the LEFT edge - the exact failure the width
          argument exists to prevent, arriving on the axis the wrap introduced. */}
      {anyUsable && (
        <div className="flex min-h-0 flex-1 flex-col flex-wrap-reverse content-start items-start gap-1.5 self-end">
          {/* GIMBAL, as a CROSS rather than a row. CTR was once the only
              non-drag path to the gimbal, so an operator who could not drag
              could recenter the camera and nothing else — acute on the details
              panel, which scrolls and so disabled dragging outright, leaving
              the FEED tab with no way to aim at all. That panel draws no picture
              now, but the keys stay: they are the gesture hook's F10 follow-up,
              and they are the only aiming path for a pointer that cannot drag.

              THE SHAPE IS LOAD-BEARING, for two reasons a row got wrong.

              Width. Laid out in a line, five 40 px keys plus the zoom group
              needed 344 px of picture. The details panel is `overflow-hidden`
              and its picture is 262 px at the 288 px minimum panel width, so the
              left keys were clipped off the box — still focusable, still firing,
              invisible. The cross is at most three keys wide (120 px, and only
              40 px on a pitch-only payload), which fits every surface and stops
              the cluster painting over the pose readout anchored opposite it.
              That readout carries the `G✓`/`G✗` glyph and the pitch number — the
              only evidence a nudge did anything — so hiding it behind the key
              that produced it is the one overlap that must not happen.

              Reach. In a row, `up` and `down` are neighbours separated by a 1 px
              hairline, so a gloved mis-tap reverses the commanded direction. In
              a cross they sit at opposite ends of the CTR key. It also fixes a
              position shift: keys hold their places when an axis is absent,
              instead of `up` sliding into where `down` used to be.

              RENDER on what the PAYLOAD has. An axis this airframe does not have
              is debris that never becomes useful. On the Mavic 3T `gimbal_yaw`
              is false, so this degenerates to a vertical up/CTR/down strip — a
              shape that reads as "tilt" on sight, where a horizontal pair of
              vertical arrows did not.

              Rendered in `compact` too. The earlier version hid the keys there
              on a width argument that the cross removes: 3 × 32 px is 96 px, and
              the MAP-layout stage is the surface where a hand is on the aircraft
              and the picture may not be grabbable at all (a dead player or an
              RTMP branch withholds the gesture surface via F9).

              The `disabled` prop is unreachable rather than load-bearing: every
              non-capability gate in `cameraCommandAvailability` blocks all three
              axes at once, which clears `anyUsable` and unmounts this cluster.
              Kept because CTR carries it for the same reason. */}
          <div
            role="group"
            aria-label="Gimbal"
            className="flex flex-col items-center overflow-hidden rounded-md border border-white/15 bg-black/70 backdrop-blur-sm"
          >
            {canPitch && (
              <>
                {/* Positive pitch is UP, matching the drag gesture:
                    `slewDeltasDeg` negates screen dy, so dragging up asks for a
                    positive pitch. A nudge key that disagreed with the drag on
                    the same picture would be its own bug report. */}
                <button
                  type="button"
                  className={controlButtonClass(compact)}
                  disabled={!pitchAvailable}
                  aria-label={`Nudge gimbal up ${GIMBAL_NUDGE_DEG} degrees`}
                  title={pitchAvailable ? undefined : availability.gimbalPitch.reason ?? undefined}
                  onClick={() => onCameraCommand?.('gimbal', { pitch_deg: GIMBAL_NUDGE_DEG })}
                >
                  <ArrowUp className="size-4" aria-hidden />
                </button>
                <KeyDivider stacked />
              </>
            )}
            <div className="flex items-center">
              {canYaw && (
                <>
                  <button
                    type="button"
                    className={controlButtonClass(compact)}
                    disabled={!yawAvailable}
                    aria-label={`Nudge gimbal left ${GIMBAL_NUDGE_DEG} degrees`}
                    title={yawAvailable ? undefined : availability.gimbalYaw.reason ?? undefined}
                    onClick={() => onCameraCommand?.('gimbal', { yaw_deg: -GIMBAL_NUDGE_DEG })}
                  >
                    <ArrowLeft className="size-4" aria-hidden />
                  </button>
                  <KeyDivider />
                </>
              )}
              <button
                type="button"
                className={controlButtonClass(compact)}
                disabled={!gimbalUsable}
                aria-label="Center gimbal"
                title={gimbalUsable ? undefined : availability.gimbalPitch.reason ?? undefined}
                onClick={() => onCameraCommand?.('gimbal', { center: true })}
              >
                <Crosshair className={compact ? 'size-3.5' : 'size-4'} aria-hidden />
              </button>
              {canYaw && (
                <>
                  <KeyDivider />
                  <button
                    type="button"
                    className={controlButtonClass(compact)}
                    disabled={!yawAvailable}
                    aria-label={`Nudge gimbal right ${GIMBAL_NUDGE_DEG} degrees`}
                    title={yawAvailable ? undefined : availability.gimbalYaw.reason ?? undefined}
                    onClick={() => onCameraCommand?.('gimbal', { yaw_deg: GIMBAL_NUDGE_DEG })}
                  >
                    <ArrowRight className="size-4" aria-hidden />
                  </button>
                </>
              )}
            </div>
            {canPitch && (
              <>
                <KeyDivider stacked />
                <button
                  type="button"
                  className={controlButtonClass(compact)}
                  disabled={!pitchAvailable}
                  aria-label={`Nudge gimbal down ${GIMBAL_NUDGE_DEG} degrees`}
                  title={pitchAvailable ? undefined : availability.gimbalPitch.reason ?? undefined}
                  onClick={() => onCameraCommand?.('gimbal', { pitch_deg: -GIMBAL_NUDGE_DEG })}
                >
                  <ArrowDown className="size-4" aria-hidden />
                </button>
              </>
            )}
          </div>

          <div className="flex items-center overflow-hidden rounded-md border border-white/15 bg-black/70 backdrop-blur-sm">
            <button
              type="button"
              className={controlButtonClass(compact)}
              disabled={!zoomAvailable}
              title={zoomAvailable ? 'Zoom out' : availability.zoom.reason ?? undefined}
              aria-label="Zoom out"
              onClick={() => onCameraCommand?.('zoom', { step: 'out' })}
            >
              <Minus className={compact ? 'size-3.5' : 'size-4'} aria-hidden />
            </button>
            <span aria-hidden className="h-5 w-px shrink-0 bg-white/15" />
            <button
              type="button"
              className={controlButtonClass(compact)}
              disabled={!zoomAvailable}
              title={zoomAvailable ? 'Zoom in' : availability.zoom.reason ?? undefined}
              aria-label="Zoom in"
              onClick={() => onCameraCommand?.('zoom', { step: 'in' })}
            >
              <Plus className={compact ? 'size-3.5' : 'size-4'} aria-hidden />
            </button>
            <span aria-hidden className="h-5 w-px shrink-0 bg-white/15" />
            {/* "Reset zoom to the minimum", NOT "to 1×". This camera cannot
                reach 1×: `KeyCameraZoomRatios` has a measured floor of 1.5142 on
                M-001, so the reset lands there and the readout keeps reading
                ZM 1.5×. Promising a number the hardware will not produce is how
                an operator concludes the button is broken. */}
            <button
              type="button"
              className={controlButtonClass(compact)}
              disabled={!zoomAvailable}
              title={zoomAvailable ? 'Reset zoom to the minimum' : availability.zoom.reason ?? undefined}
              aria-label="Reset zoom"
              onClick={() => onCameraCommand?.('zoom', { step: 'reset' })}
            >
              <RotateCcw className={compact ? 'size-3.5' : 'size-4'} aria-hidden />
            </button>
          </div>

          {/* LENS — which camera feeds the one stream. Words, not icons: three
              lenses have no glyphs an operator reads correctly at a glance, and
              the RC's own LENS row already labels them WIDE / ZOOM / IR, so an
              icon here would be a second vocabulary for one camera.

              THIS IS THE WIDEST GROUP IN THE COLUMN and that is the constraint
              to respect if it changes. The column is `items-end` and only as
              wide as its widest child — three text keys are ~140 px against the
              zoom trio's 120 px, and the binding surface is the details panel,
              whose picture is 262 px at the 288 px minimum panel width. It fits,
              with room; a fourth lens or a longer label would need re-measuring
              against that number, not against this file.

              Rendered only when the payload reports the capability, so an
              airframe with one camera shows no lens group at all rather than
              three keys that refuse. Which lenses a capable airframe HAS is a
              different question the heartbeat cannot answer — a Mavic 3E has no
              thermal — so those refuse on press and say so on the failure line.

              Kept in `compact` too: 3 keys of ~34 px is ~110 px, and the MAP
              stage is exactly where an operator is switching to thermal to find
              something. */}
          {lensAvailable && (
            <div
              role="group"
              aria-label="Lens"
              className="flex items-center overflow-hidden rounded-md border border-white/15 bg-black/70 backdrop-blur-sm"
            >
              {LENS_ORDER.map((lens, i) => {
                // CONFIRMED is the camera's own answer, never the press. A key
                // is lit only when telemetry says that lens is live; the press
                // shows as AWAITING until the readback agrees, which is the one
                // state that distinguishes "it worked" from "it was sent".
                //
                // AWAITING is gated on the command still being IN FLIGHT, not on
                // `requestedLens` alone, and both halves of that are load-bearing.
                // `requestedLens` is sticky until the operator selects another
                // aircraft, so on its own it produced two lies:
                //
                //  - a REFUSED lens stayed amber for the rest of the flight. The
                //    failure line and the L✗ glyph retire after
                //    ACK_TERMINAL_CLEAR_MS, so an operator who looked away for
                //    six seconds saw a camera that had been "switching to
                //    thermal" for twenty minutes, with nothing left on screen
                //    saying it had been refused.
                //  - it RESURRECTED. After a confirmed switch the value stays
                //    set, so if the lens later changed back - the pilot's own
                //    LENS row on the RC, a mission action, an SDK reset -
                //    `confirmed` went false and this went true again, lighting a
                //    key amber that nobody had pressed.
                //
                // The ack lifecycle already knows when a request stops being in
                // flight; asking it is what makes both cases terminate.
                const confirmed = cameraLens === lens;
                const lensInFlight = lensPhase === 'pending' || lensPhase === 'acked';
                const awaiting = requestedLens === lens && !confirmed && lensInFlight;
                return (
                  <Fragment key={lens}>
                    {i > 0 && <KeyDivider />}
                    <button
                      type="button"
                      className={[
                        controlButtonClass(compact),
                        // Width is content-driven here, unlike every other key
                        // in this bar: `size-8`/`size-10` would clip "WIDE".
                        compact ? 'w-auto px-2 text-[10px]' : 'w-auto px-2.5 text-[11px]',
                        'font-semibold tracking-wider',
                        confirmed ? 'bg-on-media-green-fg/20 text-on-media-green-fg' : '',
                        awaiting ? 'bg-on-media-amber-fg/20 text-on-media-amber-fg' : '',
                      ].join(' ')}
                      // `aria-pressed` follows the CONFIRMED lens for the same
                      // reason the fill does: a screen-reader user must not be
                      // told the camera switched before it did.
                      aria-pressed={confirmed}
                      aria-label={`${LENS_LABELS[lens]} lens${confirmed ? ' (live)' : awaiting ? ' (requested, not confirmed)' : ''}`}
                      title={`Switch the video stream to the ${LENS_LABELS[lens]} lens`}
                      onClick={() => onCameraCommand?.('lens', { lens })}
                    >
                      {LENS_LABELS[lens]}
                    </button>
                  </Fragment>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* The bottom band carries WORDS only — refusals, failures, and whatever
          the caller stacks beneath them. It has no ground of its own: each line
          paints its own, so an overlay with nothing to say leaves the picture
          alone instead of laying an empty black strip across it.

          `mt-auto` IS LOAD-BEARING and is not a tidy-up. In the stack the
          readout and this band are both `flex-none`, so the ONLY child that can
          absorb free space is the rail's `flex-1` — and the rail is behind
          `anyUsable`. That is false for most of the fleet, not an edge case:
          viewers, RemoteID tracks, lost links, every DJI Cloud dock aircraft and
          every MAVLink airframe (camera verbs are companion-only), an offline
          companion, unreported capabilities. With nothing growing, the band
          packed directly under the readout and a line reading
          `CAMERA CONTROL UNAVAILABLE …` - or the manual stage's
          `PICTURE STALE — DO NOT USE` - rendered as a caption on the readout
          instead of a status band on the picture's bottom edge, which is where
          it lived when it was `absolute inset-x-0 bottom-0`.

          The auto margin is a no-op when the rail IS present: flex resolves
          grow before auto margins, so there is no free space left for it to
          take. */}
      <div className="-mx-2 -mb-2 mt-auto flex flex-none flex-col">
        {/* Failure detail as VISIBLE text, per verb — a tooltip does not exist
            on touch (F3a). The region is always mounted so aria-live announces
            the failure when it lands (F3d); the hook auto-clears the entry,
            which clears this line with it. */}
        <div
          aria-live="polite"
          data-testid="camera-failure-line"
          className={`${compact ? 'text-[9px]' : 'text-[10px]'} tracking-wide text-on-media-red-fg`}
        >
          {gimbalFailure !== null && (
            <span className="block bg-black/85 px-2 py-0.5">{`GBL ✗ ${gimbalFailure}`}</span>
          )}
          {zoomFailure !== null && (
            <span className="block bg-black/85 px-2 py-0.5">{`ZM ✗ ${zoomFailure}`}</span>
          )}
          {/* This line is load-bearing for lens in a way it is not for the other
              two: the capability says the payload accepts a lens command, not
              which lenses exist, so "this airframe has no thermal camera" can
              only ever reach the operator here, in the aircraft's own words. */}
          {lensFailure !== null && (
            <span className="block bg-black/85 px-2 py-0.5">{`LN ✗ ${lensFailure}`}</span>
          )}
        </div>

        {/* Some controls withheld while others work: say why, visibly (F3c). */}
        {partialReasons.length > 0 && (
          <div
            className={`bg-black/85 px-2 py-0.5 ${
              compact ? 'text-[9px]' : 'text-[10px]'
            } uppercase tracking-wider text-on-media-muted-fg`}
          >
            {partialReasons.join(' · ')}
          </div>
        )}

        {/* Nothing usable: one muted line, never instead of the readout (F4).
            RemoteID tracks, lost links, dead companions and unreported payloads
            all land here via the availability inputs. */}
        {!anyUsable && (
          <div
            className={`bg-black/85 px-2 py-0.5 text-center ${
              compact ? 'text-[9px]' : 'text-[10px]'
            } uppercase tracking-wider text-on-media-muted-fg`}
          >
            {`CAMERA CONTROL UNAVAILABLE — ${availability.unavailableReason ?? 'NOT SUPPORTED'}`}
          </div>
        )}

        {/* Last in the stack, so it sits at the very bottom of the picture —
            see `footer`. Rendered inside the `pointer-events-none` container,
            so a footer with interactive parts must re-enable them itself. */}
        {footer}
      </div>
      </div>
    </>
  );
}
